import React, { FC } from 'react';
import { ExareStatus } from '@websky/graphql';
import { ExchangeRefundOrderRequestStatus } from '@entities/order';
import { REFUND_SERVICES_ROUTES } from '@entities/refund-services';
import { useTheme } from '../../../../../../theme';
import { useTranslation } from 'react-i18next';
import { useHistory } from '@shared/routing';

export interface Props {
	show: boolean;
	status: ExareStatus;
	orderId: string;
}

const RefundServicesStatus: FC<Props> = ({ show, status, orderId }) => {
	const { t } = useTranslation('RefundServices');
	const { RefundServicesStatus: theme } = useTheme('Features').RefundServices;
	const history = useHistory();

	const goToRequest = () => {
		history.push(`/booking/${orderId}${REFUND_SERVICES_ROUTES.REQUEST}`);
	};

	const notificationStatus = (() => {
		switch (status) {
			case ExareStatus.InProcess:
			case ExareStatus.NeedConfirm:
			case ExareStatus.AwaitingPayment:
				return 'warning';

			case ExareStatus.Rejected:
			case ExareStatus.Refused:
			case ExareStatus.TimelimitExpired:
				return 'error';

			case ExareStatus.Finished:
				return 'done';

			default:
				return 'warning';
		}
	})();

	const notificationTitle: string = (() => {
		switch (status) {
			case ExareStatus.InProcess:
				return t(
					'Your refund request is currently being processed. We will notify you once the review is complete.'
				);
			case ExareStatus.NeedConfirm:
				return t(
					'Please confirm your refund request to proceed. Check your email or notifications for the confirmation link.'
				);
			case ExareStatus.AwaitingPayment:
				return t(
					'The refund has been approved and is awaiting transfer. Funds will be sent to your card shortly.'
				);
			case ExareStatus.Finished:
				return t('The services have been successfully refunded, and the funds have been sent to your card.');
			case ExareStatus.Rejected:
				return t(
					'Your refund request has been rejected. Please contact support if you believe this is an error.'
				);
			case ExareStatus.Refused:
				return t(
					'The refund request was refused. The service does not meet the criteria for a refund under our current policy.'
				);
			case ExareStatus.TimelimitExpired:
				return t(
					'The refund request has expired. The time limit for submitting or completing this request has been reached.'
				);
			default:
				return '';
		}
	})();

	const action = (() => {
		switch (status) {
			case ExareStatus.InProcess:
			case ExareStatus.NeedConfirm:
			case ExareStatus.AwaitingPayment:
			case ExareStatus.Rejected:
			case ExareStatus.Refused:
			case ExareStatus.TimelimitExpired:
				return {
					actionText: t('Request'),
					onClick: goToRequest
				};

			case ExareStatus.Finished:
				return null;

			default:
				return null;
		}
	})();

	if (!show) {
		return null;
	}

	return (
		<div className={theme.wrapper}>
			<ExchangeRefundOrderRequestStatus
				status={notificationStatus}
				title={notificationTitle}
				actionText={action?.actionText}
				onClickAction={action?.onClick}
			/>
		</div>
	);
};

export default RefundServicesStatus;
